import React, { useEffect, useState, useContext, useRef, useCallback } from "react";
import { connect } from "react-redux";
import responseHandler from "responseHandler";
import { PhoenixCaseFormOrPreview, usePhoenixForms } from "./PhoenixFormsProvider";
import type * as types from "./PhoenixFormsV4";
import { MSpinner } from "components/Shared";
import PhoenixFormsV4Section from "./Controls/PhoenixFormsV4Section";
import { Button } from "react-md";
import { PolicyInfo, PreviewUtilsRequest } from "interfaces/Mueller.Business.Models";
import { WebApiRequest, HTTP_POST } from "tools/webservice";
import { DialogContext, DialogProviderValue } from "components/Shared/DialogProvider.tsx";
import PhoenixFormValidation from "components/Homeowner/CaseForms/PhoenixFormValidation";
import { getCaseInfo, getCaseRCT } from "actions/qualityAssurance";
import { userHasRight } from "selectors/rights";
import { CASE_QUALITY_REVIEW, FORM_DESIGN_EDIT } from "typeConstants/rights";
import { getPolicyInfo } from "requests/homeowner/ho";
import { webApiDownloadFile } from "tools/fileDownloadTools";
import Select from "react-select";

interface Props {
  form: PhoenixCaseFormOrPreview;
  readonly: boolean;
  showFormNameHeader?: boolean;
  showUtilities?: boolean;

  // redux :(
  isQAUser: boolean;
  makeToast: (text: string, type: string) => void;
  getCaseRCT: (req: any) => void;
  getQaCaseInfo: (req: any, callback?: (data: any) => void) => any;
  controlNumber: number;
  qaCase: PolicyInfo;
  hasFormDesignEditRight: boolean;
};

type PreviewOperation = "xmldata" |
  "xsd" |
  "xform" |
  "export-dictionary" |
  "export-full" |
  "export-definition" |
  "rule-results" |
  "recommendation-results";

const PhoenixFormInstance = (
  {
    form,
    readonly,
    showFormNameHeader = false,
    showUtilities = false,
    isQAUser,
    makeToast,
    getCaseRCT,
    getQaCaseInfo,
    controlNumber,
    qaCase,
    hasFormDesignEditRight,
  }: Props
) => {
  const mounted = useRef<boolean>(true);
  const dialog: DialogProviderValue = useContext(DialogContext);

  const { isLoaded, didFailLoading, isSaving, needsSave, validationErrors, ruleResults, getFormData, save, reload, jumpToControl, isPreview, getProxy } = usePhoenixForms();
  const [formData, setFormData] = useState<types.PhoenixForm>(null);
  const [isRunningRctRequest, setIsRunningRctRequest] = useState<boolean>(false);
  const [lastCalculationFailMessage, setLastCalculationFailMessage] = useState<string>(null);
  const [isRunningCommit, setIsRunningCommit] = useState<boolean>(false);
  const [isRunningUpdateRecommendations, setIsRunningUpdateRecommendations] = useState<boolean>(false);
  const [isRunningPreviewUtil, setIsRunningPreviewUtil] = useState<boolean>(false);

  const previousFormId = useRef<string>(null);
  const containerRef = useRef<HTMLDivElement>();
  const wasSaving = useRef<boolean>();
  const scrollPositionY = useRef<number>(0);

  // form loader
  useEffect(() => {
    if (formData?.id) {
      previousFormId.current = formData.id;
    }
    let newFormData = getFormData(form);
    setFormData(newFormData);
    
    if (formData && newFormData && formData.id != newFormData.id) {
      if (containerRef.current) containerRef.current.scrollTop = 0;
    }
  }, [isLoaded, form]);

  // refresh rct after save
  useEffect(() => {
    if (form.rct && wasSaving.current && !isSaving) {
      refreshRCTIfQA();
    }

    wasSaving.current = isSaving;
  }, [isSaving, form]);

  useEffect(() => {
    if(isRunningCommit) {
      if((qaCase.coverageAOut && qaCase.coverageAOut > 0) && (qaCase.coverageAIn && qaCase.coverageAIn > 0)) {
        getPolicyInfo(controlNumber, (ch: PolicyInfo) => {
          let itv = qaCase.coverageAIn/qaCase.coverageAOut * 100;
          if(itv < ch.qaQueueRules.qaRuleItvMgrReviewMin || itv > ch.qaQueueRules.qaRuleItvMgrReviewMax) {
            dialog.showOk("Replacement Cost", "You MUST consult with a manager prior to completing this report.");
          }
          else if(itv < ch.qaQueueRules.qaRuleItvWarningMin || itv > ch.qaQueueRules.qaRuleItvWarningMax) {
            dialog.showOk("Replacement Cost", "Please check all data entry points within the RC for accuracy.");
          }
          setIsRunningCommit(false);
        }, mounted);
      }
      else {
        setIsRunningCommit(false);
      }
    }
  }, [qaCase])

  // set the scroll bar to where it was if we're loading up the same form after formData changes
  useEffect(() => {
    if (formData && previousFormId.current && formData.id === previousFormId.current && containerRef.current) {
      containerRef.current.scrollTop = scrollPositionY.current;
    }
  }, [formData])

  const containerCallbackRef = useCallback((node: HTMLDivElement) => {
    if (node) {
      containerRef.current = node;
      node.addEventListener("scrollend", () => {
        scrollPositionY.current = node.scrollTop;
      })
    }
  }, []);

  const refreshRCTIfQA = () => {
    // pretty awkward but the refresh endpoint is on the QA controller protected by this right.
    if (isQAUser) {
      getCaseRCT({ ControlNumber: form.controlNumber });
    }
  };

  /**shared method to backfill prior to running other operation */
  const backfillRct = (onSuccess: () => void) => {
    WebApiRequest({
      endpoint: form.rct.backfillUrl,
      method: HTTP_POST,
      successCallback: () => {
        onSuccess();
      },
      errorCallback: () => {
        setIsRunningRctRequest(false);
      },
      mountedRef: mounted,
    });
  };
  
  const calculateRCT = () => {
    setIsRunningRctRequest(true);
    backfillRct(() => {
      WebApiRequest({
        endpoint: form.rct.calculateUrl,
        method: HTTP_POST,
        successCallback: (response) => {
        if(response.Error){
          setIsRunningRctRequest(false);
          dialog.showOk("Calculation Failed", "RCT failed to calculate. Please review the error message at the top of the form");
          setLastCalculationFailMessage(response.Error);
        }
        else {
            setIsRunningRctRequest(false);
            refreshRCTIfQA();
            makeToast("Calculate Success", "success");
          if(lastCalculationFailMessage) {
            setLastCalculationFailMessage(null);
          }
        }
        },
        errorCallback: (response) => {
          setIsRunningRctRequest(false);
        dialog.showOk("Calculation Failed", `Calculation failed with message: ${response.Error}`)
        },
        mountedRef: mounted,
      });
    });
  };
  const commitRCT = () => {
    dialog.showYesNo(
      "RCT Commit", 
      <p>Commit should only be clicked once per case, are you sure you want to continue?</p>,
      commitRCTYes, 
      null,
      "Yes, Commit",
      "No, Cancel"
    );
  };
  const commitRCTYes = () => {
    setIsRunningRctRequest(true);
    backfillRct(() => {
      setIsRunningCommit(true);
      WebApiRequest({
        endpoint: form.rct.commitUrl,
        method: HTTP_POST,
        successCallback: () => {
          refreshRCTIfQA();
          getQaCaseInfo(controlNumber);
          setIsRunningRctRequest(false);
          makeToast("Commit Success", "success");
        },
        errorCallback: () => {
          setIsRunningRctRequest(false);
        },
        mountedRef: mounted,
      });
    });
  };
  const updateRecommendations = () => {
    setIsRunningUpdateRecommendations(true);
    WebApiRequest({
      endpoint: form.recommendations.updateRecommendationsUrl,
      method: HTTP_POST,
      successCallback: () => {
        setIsRunningUpdateRecommendations(false);
        reload();
      },
      errorCallback: () => {
        setIsRunningUpdateRecommendations(false);
      },
      mountedRef: mounted,
    });
  };
  const previewUtil = (operation: PreviewOperation) => {
    setIsRunningPreviewUtil(true);

    const request: PreviewUtilsRequest = {
      phoenixFormId: form.previewFormId,
      operation: operation,
      rawSaveData: JSON.stringify(getProxy().getRawSaveData()),
    }

    webApiDownloadFile(`/phoenixforms-list/preview-utils`, "POST", JSON.stringify(request), {
      completeCallback: () => {
        setIsRunningPreviewUtil(false);
      }
    })
  };

  let filteredValidationErrors = [];
  if (validationErrors?.length) {
    filteredValidationErrors = validationErrors.filter(a => a.formId === form.formId);
  }
  
  let filteredFormWarnings: types.ValidationError[] = [];
  if (ruleResults?.length && form.formWarningsRulesetIds?.length) {
    filteredFormWarnings = ruleResults
      .filter(a => form.formWarningsRulesetIds.includes(a.formId))
      .map<types.ValidationError>(a => ({
          controlId: 0,
          label: "",
          formId: a.formId,
          errorMessage: a.name,
          fullErrorMessage: a.name,
      }));
  }

  if (didFailLoading) {
    return (
      <div className="phoenixform-v4-container">
        <div className="phoenixform-v4">
          <div className="header">
            <Button raised primary onClick={reload}>Reload</Button>
          </div>
          <div className="error">
            <p>Failed to load form data.</p>
            <p>Please try reloading the form.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!isLoaded || !formData) return <MSpinner />;

  const sectionCount = formData.sections.length;

  return (
    <div className="phoenixform-v4-container" ref={containerCallbackRef}>
      <div className="phoenixform-v4">
        {showFormNameHeader && <div className="phoenixform-v4-name-header">{formData.name}</div>}
        {!readonly && !isPreview && 
        <div className="header">
          <Button
              raised
              primary
              onClick={save}
              disabled={!needsSave || isSaving || isRunningRctRequest || isRunningCommit}>
              Save
          </Button>
          {form.rct && form.rct.calculateUrl &&
          <Button
            raised
            primary
            onClick={calculateRCT}
            disabled={needsSave || isSaving || isRunningRctRequest || isRunningCommit}>
            Calculate
          </Button>
          }
          {form.rct && form.rct.commitUrl && 
          <Button
            raised
            primary
            onClick={commitRCT}
            disabled={needsSave || isSaving || isRunningRctRequest || isRunningCommit}>
            Commit
          </Button>
          }
          {form.recommendations && form.recommendations.updateRecommendationsUrl &&
          <Button
            raised
            primary
            onClick={updateRecommendations}
            disabled={needsSave || isSaving || isRunningUpdateRecommendations}>
            Update Recommendations
          </Button>
          }
          {isSaving && <p>Saving...</p>}
          {lastCalculationFailMessage && <p>Last error message after calculating: {lastCalculationFailMessage}</p>}
          {(isRunningRctRequest || isRunningCommit) && <p>Executing RCT Operation...</p>}
        </div>
        }
        {isPreview && hasFormDesignEditRight && showUtilities &&
        <div className="header">
          <Select
            placeholder="Preview Utilities"
            classNamePrefix="mueller-react-select"
            isSearchable={false}
            disabled={isRunningPreviewUtil} // this doesn't seem to change styles or class names, so there's a secondary indicator below...
            value="this is fixed as-is so it always says preview utilities"
            options={[
              { label: "Xml Data", value: "xmldata" },
              { label: "Xml Schema", value: "xsd" },
              { label: "XForm", value: "xform" },
              { label: "Export Dictionary", value: "export-dictionary" },
              { label: "Full Export", value: "export-full" },
              { label: "Export Definition", value: "export-definition" },
              { label: "Rule Results", value: "rule-results" },
              { label: "Recommendation Results", value: "recommendation-results" },
            ]}
            onChange={selected => previewUtil(selected.value)}
          />
          {isRunningPreviewUtil && <p>Executing Preview Utility...</p>}
        </div>
        }
        {!readonly &&
        <div>
          <PhoenixFormValidation validationMessages={filteredValidationErrors} handleClick={item => jumpToControl(item.controlId)} title="Form Validation Issues" />
          <PhoenixFormValidation validationMessages={filteredFormWarnings} handleClick={() => {}} title="Form Validation Warnings" className="warnings" />
        </div>
        }
        <div className="controls">
          {
            formData.sections.map(s => <PhoenixFormsV4Section key={`${form.formId}-${s.name}`} headerOverride={sectionCount == 1 ? formData.name : null} section={s} readonly={readonly} />)
          }
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = state => {
  return {
    isQAUser: userHasRight(CASE_QUALITY_REVIEW)(state),
    controlNumber: state.caseDetails.controlNumber,
    qaCase: state.qualityAssurance.qaCase.caseInfo,
    hasFormDesignEditRight: userHasRight(FORM_DESIGN_EDIT)(state),
  };
};

const mapDispatchToProps = (dispatch) => ({
  makeToast(text, type) {
    responseHandler.customToast(text, type, dispatch);
  },
  getCaseRCT(payload, callback) {
    dispatch(getCaseRCT(payload, callback));
  },
  getQaCaseInfo(payload, callback) {
    dispatch(getCaseInfo(payload, callback));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(PhoenixFormInstance);